import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clapperboard, ExternalLink, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'canciller_studio',
  category: 'business',
  titleKey: 'dashboard.widgets.cancillerStudio',
  descriptionKey: 'dashboard.widgets.cancillerStudioDesc',
  defaultSize: 'xl',
  minRole: 'admin',
  icon: Clapperboard,
};

// Uso REAL de Canciller Studio (la herramienta que le hicimos a El Canciller /
// DinamarcaPR), no la salud del VPS — eso ya lo cubre el widget de VPS Health.
// La idea es contestar de un golpe de ojo: ¿la están usando?, ¿quiénes?, ¿cuánto
// producen?, ¿les está fallando? La data viene del propio Studio vía
// /api/canciller-studio/overview (proxy cacheado, admin-only).

const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('es-AR').format(n));

// Paleta por marca. El Canciller y Boga comparten la plataforma pero son negocios
// distintos: el color es lo que deja leer el reparto de un vistazo, y el total
// combinado tapaba que una marca casi no la usa.
const BRAND_COLORS = ['var(--c-brand)', '#23B5A6', '#f0883e', '#8b7cf6', '#e05561'];
const brandColor = (name, order) => {
  const i = order.indexOf(name);
  return BRAND_COLORS[(i < 0 ? 0 : i) % BRAND_COLORS.length];
};

// Una fila por marca: contesta "¿las dos la están usando?" sin promediarlas.
function BrandRow({ b, share, order, t, locale }) {
  const last = b.lastJobAt ? new Date(`${String(b.lastJobAt).replace(' ', 'T')}Z`) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--c-border)' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: brandColor(b.brand, order), flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {b.brand}
        </div>
        <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
          {b.users30}/{b.usersTotal} {t('dashboard.widgets.cancillerBrandUsers')}
          {last && !isNaN(last) ? ` · ${formatDistanceToNow(last, { addSuffix: true, locale })}` : ''}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{fmt(b.pieces30)}</div>
        <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
          {share}% · {fmt(b.pieces)} {t('dashboard.widgets.cancillerTotal')}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div style={{ flex: '1 1 120px', minWidth: 110 }}>
      <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: accent || 'var(--c-text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Barras de 30 días con tooltip propio. Cada barra = piezas generadas ese día
// (la unidad que le importa a Andy: "cuántas piezas salieron").
//
// El tooltip es propio, NO el title= nativo: el del browser tarda ~1s en aparecer,
// no se puede estilar y sólo admite texto plano. Acá querés mirar una barra alta y
// saber al instante QUIÉN la generó y en QUÉ formato, que es la pregunta real.
// Se posiciona respecto del contenedor del gráfico (position:relative) y se
// voltea al otro lado cerca de los bordes para no salirse de la tarjeta.
function DailyBars({ byDay, brandOrder, t, locale }) {
  const [hover, setHover] = useState(null); // índice de la barra hovereada
  const max = Math.max(1, ...byDay.map((d) => d.pieces || 0));
  const d = hover != null ? byDay[hover] : null;

  // Fecha legible con día de la semana ("mié 23 jul"). El día viene como
  // 'YYYY-MM-DD' (UTC del lado del Studio); se parsea a mediodía para que el
  // huso horario del browser no lo corra un día para atrás.
  const prettyDay = (day) => {
    const dt = new Date(`${day}T12:00:00`);
    return dt.toLocaleDateString(locale === enUS ? 'en-US' : 'es-AR', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  };

  const isToday = (day) => day === new Date().toISOString().slice(0, 10);

  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56 }}>
        {byDay.map((day, i) => {
          const h = Math.round(((day.pieces || 0) / max) * 100);
          const active = hover === i;
          // Apilada por marca. Si el Studio todavía no manda `brands` (o hay caché
          // vieja), cae a la barra sólida de siempre.
          const parts = (day.brands || []).filter((b) => b.pieces > 0);
          const sum = parts.reduce((a, b) => a + b.pieces, 0) || 1;
          return (
            <div
              key={day.day}
              onMouseEnter={() => setHover(i)}
              style={{
                flex: 1,
                height: `${Math.max(h, day.pieces ? 6 : 2)}%`,
                minHeight: 2,
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column-reverse',
                background: day.pieces && !parts.length ? 'var(--c-brand)' : (day.pieces ? 'transparent' : 'var(--c-border)'),
                opacity: day.pieces ? (hover == null || active ? 1 : 0.45) : 0.6,
                outline: active ? '1px solid var(--c-text)' : 'none',
                outlineOffset: 1,
                cursor: 'default',
                transition: 'opacity 0.12s',
              }}
            >
              {parts.map((b) => (
                <div
                  key={b.brand}
                  style={{ height: `${(b.pieces / sum) * 100}%`, background: brandColor(b.brand, brandOrder || []) }}
                />
              ))}
            </div>
          );
        })}
      </div>

      {d && (
        <div
          style={{
            position: 'absolute',
            bottom: 64,
            // Cerca del borde derecho se ancla a la derecha para no cortarse.
            ...(hover > byDay.length / 2
              ? { right: `${((byDay.length - 1 - hover) / byDay.length) * 100}%` }
              : { left: `${(hover / byDay.length) * 100}%` }),
            zIndex: 20,
            minWidth: 190,
            padding: '8px 10px',
            borderRadius: 8,
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
            pointerEvents: 'none',
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>{prettyDay(d.day)}</span>
            {isToday(d.day) && (
              <span style={{ color: 'var(--c-brand)', fontSize: 10, fontWeight: 600 }}>
                {t('dashboard.widgets.cancillerToday')}
              </span>
            )}
          </div>

          {d.pieces === 0 && d.jobs === 0 ? (
            <div style={{ color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.cancillerNoActivity')}</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                <span style={{ color: 'var(--c-brand)', fontWeight: 700 }}>
                  {d.pieces} <span style={{ fontWeight: 400, color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.cancillerPieces')}</span>
                </span>
                <span style={{ fontWeight: 700 }}>
                  {d.jobs} <span style={{ fontWeight: 400, color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.cancillerJobs')}</span>
                </span>
              </div>

              {/* Reparto del día por marca, con el mismo color de la barra apilada. */}
              {d.brands?.length > 0 && (
                <div style={{ marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--c-border)' }}>
                  {d.brands.map((b) => (
                    <div key={b.brand} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: brandColor(b.brand, brandOrder || []), flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.brand}</span>
                      <span style={{ color: 'var(--c-text-muted)' }}>
                        {b.pieces} {t('dashboard.widgets.cancillerPieces')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* topUsers/formats/kinds los agregó el Studio para este tooltip;
                  si el Studio todavía no está actualizado, simplemente no se muestran. */}
              {d.topUsers?.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  {d.topUsers.map((u) => (
                    <div key={u.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                        {u.name}
                      </span>
                      <span style={{ color: 'var(--c-text-muted)' }}>{u.jobs}</span>
                    </div>
                  ))}
                </div>
              )}

              {d.formats?.length > 0 && (
                <div style={{ color: 'var(--c-text-muted)', fontSize: 11, borderTop: '1px solid var(--c-border)', paddingTop: 4 }}>
                  {d.formats.map((f) => `${f.format_key} ${f.count}`).join(' · ')}
                </div>
              )}

              {d.kinds?.length > 0 && (
                <div style={{ color: 'var(--c-text-muted)', fontSize: 11 }}>
                  {d.kinds.map((k) => `${k.count} ${k.kind}`).join(' · ')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Bar({ label, value, max, right }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2, gap: 8 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: 'var(--c-text-muted)', flexShrink: 0 }}>{right}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--c-surface-2)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--c-brand)' }} />
      </div>
    </div>
  );
}

export default function CancillerStudioWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const locale = user?.language === 'en' ? enUS : es;

  const { data, isLoading } = useQuery({
    queryKey: ['canciller-studio-overview'],
    queryFn: () => api.get('/canciller-studio/overview').then((r) => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const k = data?.kpis || {};
  const byDay = data?.byDay || [];
  const topUsers = (data?.topUsers || []).slice(0, 5);
  const byFormat = (data?.byFormat || []).slice(0, 4);
  const brands = useMemo(() => data?.brands || [], [data]);
  const brandOrder = useMemo(() => brands.map((b) => b.brand), [brands]);
  const totalBrandJobs = useMemo(() => brands.reduce((a, b) => a + (b.jobs || 0), 0), [brands]);

  const pieces30 = useMemo(() => byDay.reduce((a, d) => a + (d.pieces || 0), 0), [byDay]);
  const maxUserJobs = Math.max(1, ...topUsers.map((u) => u.pieces || u.jobs || 0));
  const maxFormat = Math.max(1, ...byFormat.map((f) => f.count || 0));

  const lastJobAt = data?.recentJobs?.[0]?.created_at;
  // El Studio guarda fechas en UTC con formato SQLite ("YYYY-MM-DD HH:MM:SS"),
  // que Safari no parsea — normalizamos a ISO antes de mostrarlas.
  const lastJobDate = lastJobAt ? new Date(`${String(lastJobAt).replace(' ', 'T')}Z`) : null;

  const header = (
    <div className="card-header">
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clapperboard size={16} /> {t('dashboard.widgets.cancillerStudio')}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {lastJobDate && !isNaN(lastJobDate) && (
          <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
            {t('dashboard.widgets.cancillerLastPiece')} {formatDistanceToNow(lastJobDate, { addSuffix: true, locale })}
          </span>
        )}
        {data?.studioUrl && (
          <a
            href={data.studioUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost btn-sm btn-icon"
            title={t('dashboard.widgets.cancillerOpen')}
          >
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="card" style={{ height: '100%' }}>
        {header}
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (data && data.configured === false) {
    return (
      <div className="card" style={{ height: '100%' }}>
        {header}
        <div style={{ padding: 20, color: 'var(--c-text-muted)', fontSize: 12, display: 'flex', gap: 8 }}>
          <AlertTriangle size={16} color="var(--c-pending)" style={{ flexShrink: 0 }} />
          {t('dashboard.widgets.cancillerNotConfigured')}
        </div>
      </div>
    );
  }

  if (data && data.reachable === false && !data.stale) {
    return (
      <div className="card" style={{ height: '100%' }}>
        {header}
        <div style={{ padding: 20, color: 'var(--c-open)', fontSize: 12, display: 'flex', gap: 8 }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          {t('dashboard.widgets.cancillerUnreachable')}
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ height: '100%' }}>
      {header}
      <div style={{ padding: '4px 20px 16px' }}>
        {data?.stale && (
          <div style={{ fontSize: 11, color: 'var(--c-pending)', marginBottom: 8 }}>
            ⚠ {t('dashboard.widgets.cancillerStale')}
          </div>
        )}

        {/* KPIs: lo que se mira primero */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <Kpi
            label={t('dashboard.widgets.cancillerPieces30')}
            value={fmt(pieces30)}
            sub={`${fmt(k.totalPieces)} ${t('dashboard.widgets.cancillerTotal')}`}
            accent="var(--c-brand)"
          />
          <Kpi
            label={t('dashboard.widgets.cancillerJobs30')}
            value={fmt(k.jobs30)}
            sub={`${fmt(k.jobs7)} ${t('dashboard.widgets.cancillerLast7')}`}
          />
          <Kpi
            label={t('dashboard.widgets.cancillerActiveUsers')}
            value={k.activeUsers30 != null ? `${k.activeUsers30}/${k.totalUsers ?? '—'}` : '—'}
            sub={`${k.activeUsers7 ?? 0} ${t('dashboard.widgets.cancillerLast7')}`}
          />
          <Kpi
            label={t('dashboard.widgets.cancillerSuccess')}
            value={k.successRate != null ? `${k.successRate}%` : '—'}
            sub={k.avgSec != null ? `${k.avgSec}s ${t('dashboard.widgets.cancillerAvg')}` : null}
            accent={k.successRate != null && k.successRate < 90 ? 'var(--c-open)' : 'var(--c-resolved)'}
          />
        </div>

        {/* Tendencia 30 días, apilada por marca */}
        {byDay.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                {t('dashboard.widgets.cancillerTrend')}
              </div>
              {brandOrder.length > 1 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {brandOrder.map((name) => (
                    <span key={name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--c-text-muted)' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: brandColor(name, brandOrder) }} />
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <DailyBars byDay={byDay} brandOrder={brandOrder} t={t} locale={locale} />
          </div>
        )}

        {/* Por marca: El Canciller y Boga son negocios distintos y el total los tapa */}
        {brands.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 2 }}>
              {t('dashboard.widgets.cancillerByBrand')}
            </div>
            {brands.map((b) => (
              <BrandRow
                key={b.brand}
                b={b}
                order={brandOrder}
                share={totalBrandJobs > 0 ? Math.round((b.jobs / totalBrandJobs) * 100) : 0}
                t={t}
                locale={locale}
              />
            ))}
          </div>
        )}

        {/* Quién lo usa + qué formatos piden */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 6 }}>
              {t('dashboard.widgets.cancillerTopUsers')}
            </div>
            {topUsers.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>—</div>
            )}
            {topUsers.map((u) => (
              <Bar
                key={u.email}
                // Con qué marca trabaja: el ranking mezclaba dos equipos distintos.
                label={`${u.name || u.email}${u.brands?.length ? ` · ${u.brands.map((b) => b.brand).join(', ')}` : ''}`}
                value={u.pieces || u.jobs || 0}
                max={maxUserJobs}
                right={`${fmt(u.pieces)} ${t('dashboard.widgets.cancillerPieces')}`}
              />
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 6 }}>
              {t('dashboard.widgets.cancillerFormats')}
            </div>
            {byFormat.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>—</div>
            )}
            {byFormat.map((f) => (
              <Bar key={f.format_key} label={f.format_key} value={f.count} max={maxFormat} right={fmt(f.count)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
